import cx from 'classnames';
import PropTypes from 'prop-types';
import Tooltip from 'rc-tooltip';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import {useSelector} from 'react-redux';
import {getLoggedUserId} from '~/common/fabric/User/reducers/user';
import {Menu} from '~/SignUpSheets/Create/routes/Customize/components/Builder/Elements/SlotToolBar/mobile/Menu';
import {MenuHeader} from '~/SignUpSheets/Create/routes/Customize/components/Builder/Elements/SlotToolBar/mobile/MenuHeader';
import {MenuItem} from '~/SignUpSheets/Create/routes/Customize/components/Builder/Elements/SlotToolBar/mobile/MenuItem';
import {Button} from '~library/atoms/Button';
import {Divider} from '~library/atoms/Divider';
import {Typography} from '~library/atoms/Typography';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {DuplicateIcon} from '~library/svg/DuplicateIcon';
import {MoreIcon} from '~library/svg/MoreIcon';
import {charcoal, feedbackError, pebble} from '~libSass/base/_exports.sass';

export const GroupTools = ({
  groupId,
  onDuplicateGroup,
  onDeleteGroup,
  isDesktop = false,
  numGroups = 0,
}) => {
  const [mwOpen, setMwOpen] = useState(false);
  const isLoggedIn = useSelector(getLoggedUserId);

  useEffect(() => {
    if (isDesktop) setMwOpen(false);
  }, [isDesktop]);

  const openGroupTools = useCallback(() => {
    setMwOpen(true);
  }, []);

  const closeGroupTools = useCallback(() => {
    setMwOpen(false);
  }, []);

  const trigger = useMemo(() => (isDesktop ? ['hover', 'click'] : []), [isDesktop]);

  const DesktopContent = useMemo(
    () => (
      <div className={cx('slot-toolbar', 'group-tools', 'reveal-on-hover')}>
        {numGroups > 1 && (
          <>
            <Tooltip
              overlayClassName="slot-toolbar__tooltip"
              placement="left"
              overlay={
                <Typography
                  as="span"
                  variant="paragraph3"
                  data-qa-id="duplicate-overlay"
                  color="charcoal"
                >
                  Delete
                </Typography>
              }
              trigger={trigger}
            >
              <Button
                variant="unstyled-button"
                className={cx('slot-toolbar__button', {danger: !isDesktop})}
                data-qa-id={`group-slot-toolbar-delete-${groupId}`}
                onClick={() => onDeleteGroup(groupId)}
                disabled={!isLoggedIn || numGroups <= 1}
                size="large"
              >
                <CloseLineIcon ratio={0.75} color={pebble} />
              </Button>
            </Tooltip>
            <Divider />
          </>
        )}
        <Tooltip
          overlayClassName="slot-toolbar__tooltip"
          placement="left"
          overlay={
            <Typography
              as="span"
              variant="paragraph3"
              data-qa-id="duplicate-overlay"
              color="charcoal"
            >
              Duplicate
            </Typography>
          }
          trigger={trigger}
        >
          <Button
            variant="unstyled-button"
            className="slot-toolbar__button"
            data-qa-id={`slot-toolbar-duplicate-${groupId}`}
            onClick={() => onDuplicateGroup(groupId)}
            disabled={!isLoggedIn}
            size="large"
          >
            <DuplicateIcon color={pebble} />
          </Button>
        </Tooltip>
      </div>
    ),
    [trigger, groupId, onDuplicateGroup, isLoggedIn, onDeleteGroup, numGroups, isDesktop]
  );

  const handleDuplicate = useCallback(() => {
    onDuplicateGroup(groupId);
    closeGroupTools();
  }, [onDuplicateGroup, closeGroupTools, groupId]);

  const handleDelete = useCallback(() => {
    onDeleteGroup(groupId);
    closeGroupTools();
  }, [onDeleteGroup, closeGroupTools, groupId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const MobileContent = useMemo(
    () => [
      <MenuItem key={1} action={handleDuplicate}>
        <DuplicateIcon />
        <Typography variant="paragraph2">Duplicate</Typography>
      </MenuItem>,
      <MenuItem key={2} action={handleDelete} disabled={numGroups <= 1} type="danger">
        <CloseLineIcon ratio={0.75} color={feedbackError} style={{margin: '-0.25rem'}} />
        <Typography variant="paragraph2" color={numGroups <= 1 ? 'coin' : 'feedbackError'}>
          Delete
        </Typography>
      </MenuItem>,
    ],
    [onDuplicateGroup, groupId, numGroups, handleDuplicate, handleDelete]
  );

  return isDesktop
    ? DesktopContent
    : <>
        <Button
          onClick={openGroupTools}
          variant="transparent"
          data-qa-id="open-group-tools"
          aria-label="Open time window tools" // TODO: Abstract this string when we add support for other group types
        >
          <MoreIcon color={charcoal} />
        </Button>
        {createPortal(
          <>
            {mwOpen && (
              <Button
                variant="unstyled-button"
                className="mobile-menu__overlay"
                onClick={closeGroupTools}
                type="button"
                aria-label="Close time window tools" // TODO: Abstract this string when we add support for other group types
              />
            )}
            <Menu
              header={<MenuHeader toggleMenu={closeGroupTools} title="Edit Time Window" />} // TODO: Abstract this string when we add support for other group types
              items={MobileContent}
              menuVisible={mwOpen}
              toggleMenu={closeGroupTools}
            />
          </>,
          document.body,
          `group-tools-${groupId}`
        )}
      </>;
};

GroupTools.propTypes = {
  groupId: PropTypes.string.isRequired,
  onDuplicateGroup: PropTypes.func.isRequired,
  onDeleteGroup: PropTypes.func.isRequired,
  isDesktop: PropTypes.bool,
  numGroups: PropTypes.number,
};
